// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import { createReference, getReferenceString } from '@medplum/core';
import type { Bundle, Extension, Schedule, Slot } from '@medplum/fhirtypes';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type { AppointmentTypeDefinition } from './appointment-types';
import { COOLDOWN_BUFFER_MINUTES } from './appointment-types';

dayjs.extend(utc);
dayjs.extend(timezone);

export const AVAILABILITY_TEMPLATE_EXTENSION_URL = 'http://polarissleep.com/StructureDefinition/availability-template';
export const AVAILABILITY_OVERRIDE_EXTENSION_URL =
  'http://polarissleep.com/StructureDefinition/availability-week-override';
export const MANAGED_AVAILABILITY_EXTENSION_URL =
  'http://polarissleep.com/StructureDefinition/managed-availability-slot';
export const PRACTITIONER_TIMEZONE_EXTENSION_URL = 'http://hl7.org/fhir/StructureDefinition/timezone';
export const SCHEDULING_V2_PROVENANCE_TAG = {
  system: 'https://polarissleep.com/scheduling-engine',
  code: 'medplum-v2',
  display: 'Medplum scheduling v2',
} as const;

export const DAY_OF_WEEK_CODES = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'] as const;
export type DayOfWeekCode = (typeof DAY_OF_WEEK_CODES)[number];

export interface AvailabilityTemplateEntry {
  dayOfWeek: DayOfWeekCode;
  startTime: string;
  endTime: string;
}

export interface ReconcileScheduleAppointmentTypeAvailabilityParams {
  schedule: Schedule;
  timezone: string;
  startDate: string;
  endDate: string;
  appointmentType: AppointmentTypeDefinition;
}

export function getScheduleTimezone(scheduleActorTimezone: string | undefined): string {
  return scheduleActorTimezone || 'UTC';
}

export function readGeneralAvailability(schedule: Schedule | undefined): AvailabilityTemplateEntry[] {
  if (!schedule) {
    return [];
  }

  const entries: AvailabilityTemplateEntry[] = [];
  for (const ext of schedule.extension ?? []) {
    if (ext.url !== AVAILABILITY_TEMPLATE_EXTENSION_URL) {
      continue;
    }

    const entry = readAvailabilityEntry(ext);
    if (entry) {
      entries.push(entry);
    }
  }

  return entries;
}

export function readSpecificAvailabilityOverride(
  schedule: Schedule | undefined,
  weekStart: string
): AvailabilityTemplateEntry[] | undefined {
  if (!schedule) {
    return undefined;
  }

  for (const ext of schedule.extension ?? []) {
    if (ext.url !== AVAILABILITY_OVERRIDE_EXTENSION_URL) {
      continue;
    }

    const weekStartValue = ext.extension?.find((child) => child.url === 'weekStart')?.valueDate;
    if (weekStartValue !== weekStart) {
      continue;
    }

    const entries = (ext.extension ?? [])
      .filter((child) => child.url === 'entry')
      .map(readAvailabilityEntry)
      .filter((entry): entry is AvailabilityTemplateEntry => Boolean(entry));

    return entries;
  }

  return undefined;
}

export function getEffectiveAvailabilityForWeek(
  schedule: Schedule | undefined,
  weekStart: string
): AvailabilityTemplateEntry[] {
  return readSpecificAvailabilityOverride(schedule, weekStart) ?? readGeneralAvailability(schedule);
}

export function setGeneralAvailability(schedule: Schedule, entries: AvailabilityTemplateEntry[]): Schedule {
  const otherExtensions = (schedule.extension ?? []).filter((ext) => ext.url !== AVAILABILITY_TEMPLATE_EXTENSION_URL);
  return {
    ...schedule,
    extension: [...otherExtensions, ...entries.map((entry) => buildTemplateExtension(entry))],
  };
}

export function setSpecificAvailabilityOverride(
  schedule: Schedule,
  weekStart: string,
  entries: AvailabilityTemplateEntry[]
): Schedule {
  const otherExtensions = (schedule.extension ?? []).filter((ext) => {
    if (ext.url !== AVAILABILITY_OVERRIDE_EXTENSION_URL) {
      return true;
    }

    const overrideWeekStart = ext.extension?.find((child) => child.url === 'weekStart')?.valueDate;
    return overrideWeekStart !== weekStart;
  });

  const overrideExtension: Extension = {
    url: AVAILABILITY_OVERRIDE_EXTENSION_URL,
    extension: [
      { url: 'weekStart', valueDate: weekStart },
      ...entries.map((entry) => ({
        url: 'entry',
        extension: [
          { url: 'dayOfWeek', valueCode: entry.dayOfWeek },
          { url: 'startTime', valueTime: `${entry.startTime}:00` },
          { url: 'endTime', valueTime: `${entry.endTime}:00` },
        ],
      })),
    ],
  };

  return {
    ...schedule,
    extension: [...otherExtensions, overrideExtension],
  };
}

export function getWeekStartForDate(date: Date | string, timezoneName: string): string {
  return dayjs.tz(date, timezoneName).startOf('week').format('YYYY-MM-DD');
}

export function getWeekRange(weekStart: string, timezoneName: string): { start: string; end: string } {
  const start = dayjs.tz(`${weekStart}T00:00:00`, timezoneName).startOf('day');
  const end = start.add(6, 'day').endOf('day');
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

export function getWeekStartsInRange(startDate: string, endDate: string, timezoneName: string): string[] {
  const starts: string[] = [];
  let cursor = dayjs.tz(startDate, timezoneName).startOf('week');
  const end = dayjs.tz(endDate, timezoneName);

  while (cursor.isBefore(end) || cursor.isSame(end)) {
    starts.push(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'week');
  }

  return starts;
}

export async function reconcileScheduleAppointmentTypeAvailability(
  medplum: MedplumClient,
  params: ReconcileScheduleAppointmentTypeAvailabilityParams
): Promise<void> {
  const { schedule, timezone, startDate, endDate, appointmentType } = params;
  if (!schedule.id) {
    return;
  }

  const freshSchedule = (await medplum.readResource('Schedule', schedule.id)) as Schedule;
  const weekStarts = getWeekStartsInRange(startDate, endDate, timezone);

  for (const weekStart of weekStarts) {
    const effectiveEntries = getEffectiveAvailabilityForWeek(freshSchedule, weekStart);
    const weekRange = getWeekRange(weekStart, timezone);

    await deleteAvailabilitySlotsForAppointmentType(
      medplum,
      freshSchedule,
      weekRange.start,
      weekRange.end,
      appointmentType
    );

    if (effectiveEntries.length === 0) {
      continue;
    }

    await createAvailabilitySlotsForAppointmentType(medplum, {
      schedule: freshSchedule,
      weekStart,
      timezone,
      appointmentType,
      entries: effectiveEntries,
    });
  }
}

function buildTemplateExtension(entry: AvailabilityTemplateEntry): Extension {
  return {
    url: AVAILABILITY_TEMPLATE_EXTENSION_URL,
    extension: [
      { url: 'dayOfWeek', valueCode: entry.dayOfWeek },
      { url: 'startTime', valueTime: `${entry.startTime}:00` },
      { url: 'endTime', valueTime: `${entry.endTime}:00` },
    ],
  };
}

function readAvailabilityEntry(ext: Extension): AvailabilityTemplateEntry | undefined {
  const dayOfWeek = ext.extension?.find((entry) => entry.url === 'dayOfWeek')?.valueCode as DayOfWeekCode | undefined;
  const startTime = ext.extension?.find((entry) => entry.url === 'startTime')?.valueTime;
  const endTime = ext.extension?.find((entry) => entry.url === 'endTime')?.valueTime;

  if (!dayOfWeek || !startTime || !endTime || !DAY_OF_WEEK_CODES.includes(dayOfWeek)) {
    return undefined;
  }

  return {
    dayOfWeek,
    startTime: startTime.slice(0, 5),
    endTime: endTime.slice(0, 5),
  };
}

async function deleteAvailabilitySlotsForAppointmentType(
  medplum: MedplumClient,
  schedule: Schedule,
  startDate: string,
  endDate: string,
  appointmentType: AppointmentTypeDefinition
): Promise<void> {
  const appointmentTypeCode = appointmentType.coding.code ?? '';
  const appointmentTypeSystem = appointmentType.coding.system ?? '';
  const scheduleReference = getReferenceString(schedule);
  if (!appointmentTypeCode || !appointmentTypeSystem || !scheduleReference) {
    return;
  }

  const slots = await medplum.searchResources('Slot', [
    ['schedule', scheduleReference],
    ['status', 'free'],
    ['service-type', `${appointmentTypeSystem}|${appointmentTypeCode}`],
    ['start', `ge${startDate}`],
    ['start', `le${endDate}`],
    ['_count', '1000'],
  ]);

  const deletableIds = slots.map((slot) => slot.id).filter((id): id is string => Boolean(id));
  if (deletableIds.length === 0) {
    return;
  }

  await medplum.executeBatch({
    resourceType: 'Bundle',
    type: 'batch',
    entry: deletableIds.map((id) => ({
      request: { method: 'DELETE' as const, url: `Slot/${id}` },
    })),
  });
}

async function createAvailabilitySlotsForAppointmentType(
  medplum: MedplumClient,
  params: {
    schedule: Schedule;
    weekStart: string;
    timezone: string;
    appointmentType: AppointmentTypeDefinition;
    entries: AvailabilityTemplateEntry[];
  }
): Promise<void> {
  const { schedule, weekStart, timezone, appointmentType, entries } = params;
  const effectiveDuration = appointmentType.durationMinutes + COOLDOWN_BUFFER_MINUTES;
  const baseWeekStart = dayjs.tz(`${weekStart}T00:00:00`, timezone).startOf('day');
  const bundleEntries: Bundle['entry'] = [];

  for (const entry of entries) {
    const dayIndex = DAY_OF_WEEK_CODES.indexOf(entry.dayOfWeek);
    if (dayIndex < 0) {
      continue;
    }

    const day = baseWeekStart.add(dayIndex, 'day');
    const intervals = generateIntervals(
      day.format('YYYY-MM-DD'),
      entry.startTime,
      entry.endTime,
      effectiveDuration,
      timezone
    );
    for (const interval of intervals) {
      bundleEntries.push({
        request: { method: 'POST', url: 'Slot' },
        resource: {
          resourceType: 'Slot',
          schedule: createReference(schedule),
          status: 'free',
          start: interval.start,
          end: interval.end,
          serviceType: [{ coding: [appointmentType.coding] }],
          extension: [{ url: MANAGED_AVAILABILITY_EXTENSION_URL, valueDate: weekStart }],
          meta: {
            ...(schedule.meta?.account ? { account: schedule.meta.account } : {}),
            tag: [SCHEDULING_V2_PROVENANCE_TAG],
          },
        } satisfies Slot,
      });
    }
  }

  if (bundleEntries.length === 0) {
    return;
  }

  await medplum.executeBatch({
    resourceType: 'Bundle',
    type: 'batch',
    entry: bundleEntries,
  });
}

function generateIntervals(
  date: string,
  startTime: string,
  endTime: string,
  durationMinutes: number,
  timezoneName: string
): Array<{ start: string; end: string }> {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);
  let current = dayjs.tz(`${date}T${pad(startHour)}:${pad(startMinute)}:00`, timezoneName);
  const dayEnd = dayjs.tz(`${date}T${pad(endHour)}:${pad(endMinute)}:00`, timezoneName);
  const intervals: Array<{ start: string; end: string }> = [];

  while (
    current.add(durationMinutes, 'minute').isSame(dayEnd) ||
    current.add(durationMinutes, 'minute').isBefore(dayEnd)
  ) {
    const next = current.add(durationMinutes, 'minute');
    intervals.push({ start: current.toISOString(), end: next.toISOString() });
    current = next;
  }

  return intervals;
}

function pad(value: number): string {
  return value.toString().padStart(2, '0');
}
